import { useState, useEffect } from 'react';
import api, { cachedGet, invalidateGetCache } from '../api';
import { useT } from '../i18n';
import { useNotify } from '../NotificationContext';
import { CHAT_DONE_KEY, TODAY_DONE_KEY, CHECKLIST_DISMISS_KEY } from './FirstDayChecklist';
import { useUpgrade } from './UpgradeOverlay';
import { Briefcase, Home, Lock, Loader2 } from 'lucide-react';

// reload 后一次性 toast 的 sessionStorage 桥 (同 tab 刷新保留)
const SWITCH_TOAST_KEY = 'oz_mode_switched_to';
// 轻量埋点: 导入期间 (checklist 未完成) 切换模式次数, 供后续激活/流失分析
const SWITCH_COUNT_KEY = 'oz_checklist_mode_switches';

/**
 * 双模式切换器 (工作|生活) — 商用版 ⊇ 家用版, 商用用户可自由切换;
 * 家庭版用户原位显示升级卡 (升级商用版, Stripe 一键);
 * 企业不渲染; 试用期可切换 (W15)。切换后整页刷新 (各模式数据严格隔离)。
 */
export default function ModeSwitcher() {
  const t = useT();
  const notify = useNotify();
  const { openUpgradeOverlay } = useUpgrade();
  const [me, setMe] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    cachedGet('/auth/me').then(r => setMe(r.data?.user || null)).catch(() => {});
  }, []);

  // 切换成功后整页刷新, 刷新后读桥接标记弹一次性 toast
  useEffect(() => {
    let switchedTo = null;
    try { switchedTo = sessionStorage.getItem(SWITCH_TOAST_KEY); } catch (_) {}
    if (switchedTo === 'family' || switchedTo === 'business') {
      try { sessionStorage.removeItem(SWITCH_TOAST_KEY); } catch (_) {}
      // 新手上路已完成 (聊过第一句 + 认识今日信息) 的用户不再提示"进度已保留"
      let onboardingComplete = false;
      try {
        onboardingComplete = localStorage.getItem(CHAT_DONE_KEY) === '1'
          && localStorage.getItem(TODAY_DONE_KEY) === '1';
      } catch (_) {}
      notify.notifyInfo(
        t(switchedTo === 'family' ? 'mode.switched_life_title' : 'mode.switched_business_title'),
        onboardingComplete ? undefined : t('mode.switched_msg'),
      );
    }
  }, []);

  if (!me) return null;
  const plan = me.plan || 'business';
  const mode = me.mode || 'business';

  const switchMode = async (next) => {
    if (busy || next === mode) return;
    setBusy(true);
    try {
      await api.post('/auth/mode', { mode: next });
      // 模式已变: assistants (服务端按模式过滤) 与 auth/me 必须回源
      invalidateGetCache('/assistants');
      invalidateGetCache('/auth/me');
      try {
        sessionStorage.setItem(SWITCH_TOAST_KEY, next);
        // 未完成的新手上路: 切换模式后重新出现 (dismiss 仅隐藏 24h, 这里主动解除)
        localStorage.removeItem(CHECKLIST_DISMISS_KEY);
        trackModeSwitchDuringOnboarding();
      } catch (_) {}
      // 落地页: 切工作 → 仪表盘; 切生活 → 今日信息 (同一路由 /dashboard, 按模式渲染)
      // 目标助理解析链: 该模式最近使用(active) → 默认 → 列表首个; 无助理 → 助理列表页
      let target = '/assistants';
      try {
        const r = await cachedGet('/assistants');
        const list = Array.isArray(r.data?.assistants) ? r.data.assistants : [];
        const lastUsedId = r.data?.last_used_assistant_id ?? null;
        const pick = list.find(a => a.id === lastUsedId && a.status === 'active')
          || list.find(a => a.is_default === 1)
          || list[0];
        if (pick?.public_id) target = `/assistant/${pick.public_id}/dashboard`;
      } catch (_) {}
      window.location.href = `/cortex${target}`;
    } catch {
      setBusy(false);
    }
  };

  // 轻量埋点: checklist 未全部完成时记录切换次数 (不打扰用户, 仅数据)
  // P1-3: 函数声明(提升) — 修复 no-use-before-define (无 this/词法依赖, 语义等价)
  function trackModeSwitchDuringOnboarding() {
    try {
      const chatDone = localStorage.getItem(CHAT_DONE_KEY) === '1';
      const todayDone = localStorage.getItem(TODAY_DONE_KEY) === '1';
      if (chatDone && todayDone) return;
      const n = parseInt(localStorage.getItem(SWITCH_COUNT_KEY) || '0', 10) || 0;
      localStorage.setItem(SWITCH_COUNT_KEY, String(n + 1));
    } catch (_) {}
  }

  // 家庭版: 保留 Life/Work 双按钮, Work 为升级入口 (🔒+升级徽标), 点击打开升级浮层
  if (plan === 'family') {
    return (
      <div
        className="flex items-center gap-1 p-1 rounded-lg"
        style={{ backgroundColor: 'var(--bg-hover)', border: '1px solid var(--border)' }}
        role="tablist"
        aria-label="mode"
      >
        <button
          className="flex items-center justify-center gap-1.5 flex-1 px-2 py-1.5 rounded-md text-xs font-semibold"
          style={{ backgroundColor: 'var(--brand)', color: '#fff' }}
        >
          <Home size={13} />
          {t('mode.life')}
        </button>
        <button
          onClick={() => openUpgradeOverlay('mode_switch')}
          className="flex items-center justify-center gap-1.5 flex-1 px-2 py-1.5 rounded-md text-xs font-semibold transition-colors"
          style={{ color: 'var(--text-secondary)' }}
        >
          <Briefcase size={13} />
          {t('mode.work')}
          <span
            className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded text-[10px] font-semibold"
            style={{ background: 'rgba(234,179,8,0.15)', color: '#b45309', border: '1px solid rgba(234,179,8,0.3)' }}
          >
            <Lock size={9} />
            {t('mode.upgrade_badge')}
          </span>
        </button>
      </div>
    );
  }

  // W15: 试用期也可切换场景轨道 (初次建助理向导已默认商用)
  if (plan !== 'business' && plan !== 'trial') return null;

  const btn = (key, Icon, label) => {
    const active = mode === key;
    return (
      <button
        onClick={() => switchMode(key)}
        disabled={busy}
        className="flex items-center justify-center gap-1.5 flex-1 px-2 py-1.5 rounded-md text-xs font-semibold transition-colors"
        style={{
          backgroundColor: active ? 'var(--brand)' : 'transparent',
          color: active ? '#fff' : 'var(--text-secondary)',
        }}
      >
        <Icon size={13} />
        {label}
      </button>
    );
  };

  return (
    <div
      className="flex items-center gap-1 p-1 rounded-lg"
      style={{ backgroundColor: 'var(--bg-hover)', border: '1px solid var(--border)' }}
      role="tablist"
      aria-label="mode"
    >
      {btn('family', Home, t('mode.life'))}
      {btn('business', Briefcase, t('mode.work'))}
    </div>
  );
}
